/**
 * CI provider auto-detection via environment variables.
 * Strategy pattern: check providers in priority order, first match wins.
 */

import type { CIMetadata, CIProvider } from '@testrelic/core';

type DetectFn = (env: Record<string, string | undefined>) => CIMetadata | null;

function detectGitHubActions(env: Record<string, string | undefined>): CIMetadata | null {
  if (env.GITHUB_ACTIONS !== 'true') return null;
  return {
    provider: 'github-actions',
    buildId: env.GITHUB_RUN_ID ?? null,
    commitSha: env.GITHUB_SHA ?? null,
    branch: env.GITHUB_REF_NAME ?? null,
    runUrl: env.GITHUB_SERVER_URL && env.GITHUB_REPOSITORY && env.GITHUB_RUN_ID
      ? `${env.GITHUB_SERVER_URL}/${env.GITHUB_REPOSITORY}/actions/runs/${env.GITHUB_RUN_ID}`
      : null,
  };
}

function detectGitLabCI(env: Record<string, string | undefined>): CIMetadata | null {
  if (env.GITLAB_CI !== 'true') return null;
  return {
    provider: 'gitlab-ci',
    buildId: env.CI_PIPELINE_ID ?? null,
    commitSha: env.CI_COMMIT_SHA ?? null,
    branch: env.CI_COMMIT_BRANCH ?? env.CI_COMMIT_REF_NAME ?? null,
    runUrl: env.CI_PIPELINE_URL ?? null,
  };
}

function detectJenkins(env: Record<string, string | undefined>): CIMetadata | null {
  if (!env.JENKINS_URL) return null;
  let branch = env.GIT_BRANCH ?? null;
  if (branch?.startsWith('origin/')) {
    branch = branch.slice('origin/'.length);
  }
  return {
    provider: 'jenkins',
    buildId: env.BUILD_ID ?? null,
    commitSha: env.GIT_COMMIT ?? null,
    branch,
    runUrl: env.BUILD_URL ?? null,
  };
}

function detectCircleCI(env: Record<string, string | undefined>): CIMetadata | null {
  if (env.CIRCLECI !== 'true') return null;
  return {
    provider: 'circleci',
    buildId: env.CIRCLE_BUILD_NUM ?? null,
    commitSha: env.CIRCLE_SHA1 ?? null,
    branch: env.CIRCLE_BRANCH ?? null,
    runUrl: env.CIRCLE_BUILD_URL ?? null,
  };
}

function detectBitbucketPipelines(env: Record<string, string | undefined>): CIMetadata | null {
  if (!env.BITBUCKET_PIPELINE_UUID) return null;
  const runUrl = env.BITBUCKET_WORKSPACE && env.BITBUCKET_REPO_SLUG && env.BITBUCKET_PIPELINE_UUID
    ? `https://bitbucket.org/${env.BITBUCKET_WORKSPACE}/${env.BITBUCKET_REPO_SLUG}/pipelines/results/${env.BITBUCKET_PIPELINE_UUID}`
    : null;
  return {
    provider: 'bitbucket-pipelines',
    buildId: env.BITBUCKET_BUILD_NUMBER ?? null,
    commitSha: env.BITBUCKET_COMMIT ?? null,
    branch: env.BITBUCKET_BRANCH ?? null,
    runUrl,
  };
}

const detectors: DetectFn[] = [
  detectGitHubActions,
  detectGitLabCI,
  detectJenkins,
  detectCircleCI,
  detectBitbucketPipelines,
];

export function detectCI(env?: Record<string, string | undefined>): CIMetadata | null {
  const envVars = env ?? process.env;
  for (const detect of detectors) {
    const result = detect(envVars);
    if (result) return result;
  }
  return null;
}
